import { format } from "date-fns";

import DateRangePicker from "@/components/DateRangePicker";

const NOON_SUFFIX = "T12:00:00";

interface PeriodRange {
  from: Date;
  to: Date;
}

interface PeriodRangeFilterProps {
  start?: string;
  end?: string;
  /** Recebe início e fim juntos (o range só é emitido completo). Ambos undefined ao limpar. */
  onChange: (start: string | undefined, end: string | undefined) => void;
  label?: string;
  /**
   * Variante alinhada aos selects vizinhos (ex.: filtros da tabela): sem label
   * acima, fundo branco e o `label` vira o placeholder dentro da caixa.
   */
  inline?: boolean;
  allowFutureDates?: boolean;
  buttonClassName?: string; 
  minDate?: string;
  maxDate?: string;
}

/**
 * Botão branco que casa com os SelectInput vizinhos (h-8, borda cinza, texto
 * escuro). `border-solid` é necessário porque a variante default do Button vem com
 * `border-none` (a borda não apareceria só com `border`).
 */
const INLINE_BUTTON_CLASS =
  "h-8 w-[230px] px-3 py-1 text-xs bg-white hover:bg-white text-slate-900 " +
  "border-solid border border-[#d1d5db] hover:border-[#f4c83e]";

const STACKED_BUTTON_CLASS = "h-8 w-[250px] px-3 py-1 text-xs";

/**
 * Filtro de período unificado (um único range picker) das telas de Conciliação de
 * Depósitos. Início e fim são escolhidos no mesmo calendário em modo range, então
 * é estruturalmente impossível o fim ser anterior ao início, e a busca só dispara
 * quando o período está completo (o DateRangePicker só emite com as duas pontas).
 * Converte entre a string `yyyy-MM-dd` do filtro e o `{from,to}` do picker; a
 * `key` reflete mudanças externas (ex.: limpar filtros) sem estado preso.
 */
export default function PeriodRangeFilter({
  start,
  end,
  onChange,
  label = "Período",
  inline = false,
  buttonClassName,
  allowFutureDates = false,
  minDate,
  maxDate,
}: PeriodRangeFilterProps) {
  const range: PeriodRange | undefined =
    start && end
      ? {
          from: new Date(start + NOON_SUFFIX),
          to: new Date(end + NOON_SUFFIX),
        }
      : undefined;

  const minBound: Date | undefined = minDate ? new Date(minDate + NOON_SUFFIX) : undefined;
  const maxBound: Date | undefined = maxDate ? new Date(maxDate + NOON_SUFFIX) : undefined;

  const handleRange = (next: PeriodRange | undefined) => {
    if (!next) {
      onChange(undefined, undefined);
      return;
    }
    onChange(format(next.from, "yyyy-MM-dd"), format(next.to, "yyyy-MM-dd"));
  };

  const resolvedButtonClass =
    buttonClassName ?? (inline ? INLINE_BUTTON_CLASS : STACKED_BUTTON_CLASS);

  if (inline) {
    return (
      <DateRangePicker
        key={`${start ?? ""}-${end ?? ""}`}
        date={range}
        onDateChange={handleRange}
        allowUnlimited
        placeholder={label}
        buttonClassName={resolvedButtonClass}
        allowFutureDates={allowFutureDates}
        minDate={minBound}
        maxDate={maxBound}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
      <DateRangePicker
        key={`${start ?? ""}-${end ?? ""}`}
        date={range}
        onDateChange={handleRange}
        allowUnlimited
        buttonClassName={resolvedButtonClass}
        minDate={minBound}
        maxDate={maxBound}
      />
    </div>
  );
}
